import { type Control, type UseFormWatch, useController } from 'react-hook-form';
import { Box, Column, Row, Text } from '~/commonComponents';
import { UserProfession } from '~/enums/UserProfession';
import { Colors } from '~/themes/colors';
import { getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import { ReadOnlyField } from '../EditShift/ReadOnlyField';
import { AssistedHygieneField } from '../ShiftDetails/ShiftDetailsForm/fields/AssistedHygieneField';
import { ExpectedProceduresField } from '../ShiftDetails/ShiftDetailsForm/fields/ExpectedProcedures';
import { MealBreakDurationField } from '../ShiftDetails/ShiftDetailsForm/fields/MealBreakDurationField';
import { MealBreakPaidField } from '../ShiftDetails/ShiftDetailsForm/fields/MealBreakPaidField';
import { ProfessionalPayField } from '../ShiftDetails/ShiftDetailsForm/fields/ProfessionalPayField';
import { TimeFieldsWrapper } from '../ShiftDetails/ShiftDetailsForm/fields/TimeFieldsWrapper';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { type IEditOpenShiftFormValues } from './useEditOpenShift';

interface IEditOpenShiftFormProps {
  control: Control<IEditOpenShiftFormValues>;
  watch: UseFormWatch<IEditOpenShiftFormValues>;
  officeName: string;
  openField: string | null;
  toggleField: (field: string) => void;
}

export const EditOpenShiftForm = ({
  control,
  watch,
  officeName,
  openField,
  toggleField,
}: IEditOpenShiftFormProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const professionCode = professionType ? UserProfession.getCode(professionType) : '';
  const isRDH = professionCode === UserProfession.RDH;
  const showProcedures = getIsAllowOfficeToPostProcedure(professionType, preferredSpecialty);

  const shiftControl = control as unknown as Control<IShiftDetailsFormValues>;

  const { field: isAssistedHygieneField } = useController({ name: 'isAssistedHygiene', control });
  const { field: arrivalField } = useController({ name: 'arrivalTime', control });
  const { field: departureField } = useController({ name: 'departureTime', control });
  const { field: mealBreakDurationField } = useController({ name: 'mealBreakDuration', control });
  const { field: isMealBreakPaidField } = useController({ name: 'isMealBreakPaid', control });
  const { field: rateField } = useController({ name: 'rate', control });

  return (
    <Column data-testid="edit-open-shift-form" gap={20}>
      <Column gap={6}>
        <Text bold fontSize={18}>
          Office:
        </Text>
        <Text
          italic
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {officeName}
        </Text>
      </Column>

      <ReadOnlyField
        label="Professional Type:"
        suffix="(Read-Only)"
        value={professionType}
        testId="profession-type-read-only"
      />

      <ReadOnlyField
        label="Preferred Specialty:"
        suffix="(Read-Only)"
        value={preferredSpecialty?.replace('specialty_', '') || ''}
        testId="preferred-specialty-read-only"
      />

      {showProcedures && (
        <>
          <Box height={1.5} backgroundColor={Colors.neutral_100} />
          <ExpectedProceduresField
            control={shiftControl}
            professionType={professionType}
            preferredSpecialty={preferredSpecialty}
          />
          <Box height={1.5} backgroundColor={Colors.neutral_100} />
        </>
      )}

      {isRDH && (
        <AssistedHygieneField
          value={isAssistedHygieneField.value}
          onChange={isAssistedHygieneField.onChange}
          isOpen={openField === 'isAssistedHygiene'}
          onToggle={() => toggleField('isAssistedHygiene')}
        />
      )}

      <Text bold fontSize={24} color="neutral_600" style={{ marginTop: 16 }}>
        Hours & Pay
      </Text>
      <Box height={1.5} backgroundColor={Colors.neutral_100} style={{ marginTop: -8 }} />

      <TimeFieldsWrapper
        arrivalValue={arrivalField.value}
        onArrivalChange={arrivalField.onChange}
        departureValue={departureField.value}
        onDepartureChange={departureField.onChange}
        openField={openField}
        toggleField={toggleField}
      />

      <Row gap={30}>
        <Column flex={1}>
          <MealBreakDurationField
            value={mealBreakDurationField.value}
            onChange={mealBreakDurationField.onChange}
            isOpen={openField === 'mealBreakDuration'}
            onToggle={() => toggleField('mealBreakDuration')}
          />
        </Column>
        <Column flex={1}>
          <MealBreakPaidField
            value={isMealBreakPaidField.value}
            onChange={isMealBreakPaidField.onChange}
            isOpen={openField === 'isMealBreakPaid'}
            onToggle={() => toggleField('isMealBreakPaid')}
          />
        </Column>
      </Row>

      <ProfessionalPayField
        value={rateField.value}
        onChange={rateField.onChange}
        isOpen={openField === 'rate'}
        onToggle={() => toggleField('rate')}
        professionType={professionType}
      />
    </Column>
  );
};
